import React, { FC, ReactNode } from 'react'

import { twMerge } from 'tailwind-merge'
import { tv, type VariantProps } from 'tailwind-variants'

import { Icon, type IconProps } from '@components/Icon'

interface ButtonIconProps extends Omit<IconProps, 'background' | 'color'> {
  background?: 'transparent' | 'black' | 'red' | 'white'
  color?: 'black' | 'red' | 'white'
}

export interface ButtonProps {
  label?: string
  children: ReactNode
  icon?: ButtonIconProps | null
  variant?:
    | 'primary'
    | 'secondary'
    | 'link'
    | 'form'
    | 'inlineLink'
    | 'brown'
    | 'black'
  className?: string
  onClick?: () => void
  href?: string
  ariaLabel?: string
  target?: string
  iconPosition?: 'left' | 'right'
  Tag?: React.ElementType
  download?: string
  hrefType?: 'email' | 'phone'
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
}

const buttonStyles = tv({
  base: 'font-[450] cursor-pointer w-fit text-left',
  variants: {
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
    },
    variant: {
      primary: 'text-black bg-[#FFEBEB] px-5 py-[10px] rounded-full',
      secondary: 'text-black bg-white px-5 py-[10px] rounded-full',
      link: 'text-grey-slate',
      inlineLink: 'text-black',
      form: 'text-black bg-[#F3F3F3] px-5 py-[10px] rounded-full',
      brown:
        'text-black bg-white border border-brown-hot px-5 py-[10px] rounded-full',
      black: 'text-black border border-deep-black px-5 py-[10px] rounded-full',
    },
    iconPosition: {
      left: '',
      right: '',
    },
  },
  compoundVariants: [
    {
      variant: ['primary', 'secondary', 'form', 'link', 'brown', 'black'],
      className: 'flex items-center justify-center gap-x-2',
    },
    {
      variant: 'inlineLink',
      className: 'inline',
    },
    {
      variant: 'primary',
      iconPosition: 'right',
      className: 'flex-row',
    },
    {
      variant: ['primary', 'secondary', 'link', 'brown', 'black'],
      iconPosition: 'left',
      className: 'flex-row-reverse',
    },
  ],
  defaultVariants: {
    variant: 'primary',
    iconPosition: 'right',
  },
})

const iconStyles = tv({
  base: '',
  variants: {
    background: {
      red: 'bg-[#EB0000] rounded-full',
      transparent: 'bg-transparent',
      white: 'bg-white rounded-full',
      black: 'bg-grey-slate rounded-full',
    },
    color: {
      white: 'text-white',
      black: 'text-black',
      red: 'text-[#EB0000]',
    },
    link: {
      true: 'inline ltr:ml-2 rtl:mr-2',
      false:
        'min-h-[22px] min-w-[22px] w-[22px] h-[22px] flex items-center justify-center',
    },
  },
  defaultVariants: {
    background: 'transparent',
    color: 'black',
  },
})

const Button: FC<ButtonProps> = ({
  label,
  children,
  icon = { name: 'arrow' },
  variant = 'primary',
  className = '',
  onClick = () => {},
  href,
  ariaLabel = '',
  target = '_self',
  iconPosition,
  type = 'button',
  Tag,
  download,
  hrefType,
  disabled,
}) => {
  const classes = twMerge(
    buttonStyles({ variant, iconPosition, disabled }),
    className,
  )

  const isInlineLinkVariant = variant === 'inlineLink'

  const content = (
    <>
      {children || label}
      {icon?.name &&
        (!isInlineLinkVariant ? (
          <div
            className={twMerge(
              iconStyles({
                background: icon.background || 'red',
                color: icon.color || 'white',
                link: isInlineLinkVariant,
              }),
              icon.className,
            )}
          >
            <Icon name={icon.name} size={icon.size || 'auto'} />
          </div>
        ) : (
          <span className="inline">
            <Icon
              name={icon.name || 'arrow'}
              size={icon.size || 'auto'}
              className={iconStyles({
                background: icon.background,
                color: icon.color || 'red',
                link: true,
              })}
            />
          </span>
        ))}
    </>
  )

  if (Tag) {
    return (
      <Tag className={classes} aria-label={ariaLabel} disabled={disabled}>
        {content}
      </Tag>
    )
  }

  const ElementType = href ? 'a' : 'button'

  switch (hrefType) {
    case 'email':
      href = `mailto:${href}`
      break
    case 'phone':
      href = `tel:${href}`
      break
    default:
      href = href
  }

  return (
    <ElementType
      onClick={onClick}
      href={href}
      className={classes}
      aria-label={ariaLabel}
      target={target}
      download={download}
      disabled={disabled}
      type={type}
    >
      {content}
    </ElementType>
  )
}

export default Button
